import { stringify } from 'query-string';
import env, { invalidateCachedAssets } from 'lib/env';
import isEmpty from 'lodash/isEmpty';

export function removeQueryParameter(url, parameter) {
  // Prefer to use l.search if you have a location/link object
  const urlparts = url.split('?');
  if (urlparts.length < 2) { return url; }

  const prefix = `${encodeURIComponent(parameter)}=`;
  const pars = urlparts[1].split(/[&;]/g);

  // Reverse iteration as may be destructive
  for (let i = pars.length; i-- > 0;) {
    // Idiom for string.startsWith
    if (pars[i].lastIndexOf(prefix, 0) !== -1) {
      pars.splice(i, 1);
    }
  }

  return urlparts[0] + (pars.length > 0 ? `?${pars.join('&')}` : '');
}

export function addQueryParameter(url, parameterName, parameterValue) {
  if (url.indexOf(parameterName) > 0) {
    return addQueryParameter(removeQueryParameter(url, parameterName), parameterName, parameterValue);
  }

  if (url.indexOf('?') > 0) {
    return `${url}&${parameterName}=${parameterValue}`;
  }

  return `${url}?${parameterName}=${parameterValue}`;
}

const TYPE_INTEGER = 1;
const TYPE_NUMBER = 3;
const TYPE_STRING = 2;

const ALLOWED_QUERYSTRING_ARGS = new Map([
  ['lat', TYPE_NUMBER],
  ['lng', TYPE_NUMBER],
  ['event_id', TYPE_INTEGER],
  ['monthly', TYPE_INTEGER],
  ['daily', TYPE_INTEGER],
  ['seller_id', TYPE_INTEGER],
  ['quote_id', TYPE_STRING],
  ['start', TYPE_STRING],
  ['end', TYPE_STRING],
  ['meta', TYPE_STRING],
  ['coupon', TYPE_STRING],
  ['event_package_ids', TYPE_STRING],
  ['event_package_id', TYPE_INTEGER],
  ['view', ['map', 'list']],
]);

export function buildCleanUrl(req) {
  const query = req.query;
  const { sanitizedQuery, outputQuery } = sanitizeQuery(query);
  const url = !isEmpty(sanitizedQuery) ? `${req.path}?${sanitizedQuery}` : req.path;
  return { url, query: outputQuery };
}

export function sanitizeQuery(query) {
  const outputQuery = {};
  for (const [key, keyType] of ALLOWED_QUERYSTRING_ARGS) {
    const queryValue = query[key];
    if (queryValue != null) {
      switch (keyType) {
        case TYPE_INTEGER:
          if (isInteger(Number(queryValue, 10))) {
            outputQuery[key] = queryValue;
          }
          break;
        case TYPE_NUMBER:
          if (isNumber(queryValue)) {
            outputQuery[key] = queryValue;
          }
          break;
        case TYPE_STRING:
          outputQuery[key] = queryValue;
          break;
        default:
          if (Array.isArray(keyType) && keyType.includes(queryValue)) {
            outputQuery[key] = queryValue;
          } else {
            delete outputQuery[key];
          }
          break;
      }
    }
  }
  const sanitizedQuery = stringify(outputQuery);
  return { sanitizedQuery, outputQuery };
}

export function isNumber(obj) {
  return !isNaN(parseFloat(obj));
}

function isInteger(value) {
  return typeof value === 'number' &&
    isFinite(value) &&
    Math.floor(value) === value;
}

export function utilStat(path) {
  const {
    CDN_HTTPS_HOST,
    GIT_HASH,
  } = env();
  let pathName;

  if (invalidateCachedAssets()) {
    if (/\?/.test(path)) {
      const [url, qstring] = path.split('?');
      const cdnQuery = btoa(qstring.contact('&gh=', GIT_HASH));
      pathName = `${url}${cdnQuery}`;
    } else {
      pathName = path.concat('.', GIT_HASH);
    }
  } else {
    pathName = path;
  }

  if (CDN_HTTPS_HOST) {
    return CDN_HTTPS_HOST.concat(pathName);
  }

  return pathName;
}

export function cdnAsset(path) {
  const {
    ASSET_CDN_HOST,
    GIT_HASH,
  } = env();

  if (!ASSET_CDN_HOST || !GIT_HASH) { return path; }

  return `${ASSET_CDN_HOST}/${GIT_HASH}${path}`;
}

export function buildRedirectURI(routerLocation) {
  if (!routerLocation) { return null; }

  const redirectURI = encodeURIComponent(`${routerLocation.pathname}${routerLocation.search}`);
  return `?redirect_uri=${redirectURI}`;
}

export function parseDomain(host = '') {
  const urlParts = host.split('.');
  const tld = urlParts.pop();
  const domain = urlParts.pop();
  const subdomain = urlParts.join('.');

  return { tld, domain, subdomain };
}

export const isValidRedirectURI = redirectURI => (
  redirectURI && !redirectURI.match(/^\/account\/sign/) && redirectURI !== 'undefined'
);
